"use client";

import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  httpBatchLink,
  httpBatchStreamLink,
  loggerLink,
  splitLink,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import SuperJSON from "superjson";

import { LOCALE_HEADER, languageOf } from "~/i18n/config";

import type { AppRouter } from "~/server/api/root";
import { createQueryClient } from "./query-client";

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  clientQueryClientSingleton ??= createQueryClient();

  return clientQueryClientSingleton;
};

export const api = createTRPCReact<AppRouter>();

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * P13-15 · Forward the page's language on every tRPC call.
 *
 * The edge middleware parses the language out of the URL and forwards it as
 * `LOCALE_HEADER`, but that only helps document requests: a client-side call
 * goes to `/api/trpc/...`, which carries no `/fr` prefix, so the server saw no
 * path language and fell back to the cookie — and a shopper who arrived at
 * `/fr` without ever choosing a language on this device has no cookie.
 *
 * The visible cost was a French shopper's checkout session, order and every
 * email about it being created in English. Sent as the same header the
 * middleware uses, so the server resolves it exactly as it does for a page:
 * as a *candidate*, still checked against the store's enabled locales.
 */
function pageLanguageHeaders(): Headers {
  const headers = new Headers();
  headers.set("x-trpc-source", "nextjs-react");
  if (typeof window !== "undefined") {
    const first = window.location.pathname.split("/")[1];
    const language = languageOf(first);
    if (language) headers.set(LOCALE_HEADER, language);
  }
  return headers;
}

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
        }),
        // P2-14: mutations must NOT stream. With a streamed response the
        // route handler has already started replying while resolvers run, so
        // Next's `revalidateTag` silently cannot take effect — cache busts
        // from admin writes would be dropped. Queries keep the stream link.
        splitLink({
          condition: (op) => op.type === "mutation",
          true: httpBatchLink({
            transformer: SuperJSON,
            url: getBaseUrl() + "/api/trpc",
            headers: pageLanguageHeaders,
          }),
          false: httpBatchStreamLink({
            transformer: SuperJSON,
            url: getBaseUrl() + "/api/trpc",
            headers: pageLanguageHeaders,
          }),
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}

function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
